import * as Sentry from '@sentry/react';

const SENTRY_DSN = (process.env.REACT_APP_SENTRY_DSN || '').trim();
const SENTRY_ENVIRONMENT = (
  process.env.REACT_APP_SENTRY_ENVIRONMENT ||
  process.env.NODE_ENV ||
  'development'
).trim();
const SENTRY_RELEASE = (process.env.REACT_APP_SENTRY_RELEASE || '').trim();

const parseSampleRate = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) return fallback;
  return parsed;
};

const parseBoolean = (value, fallback = false) => {
  if (value === undefined || value === null || value === '') return fallback;
  return ['1', 'true', 'yes', 'on'].includes(String(value).trim().toLowerCase());
};

const getTracePropagationTargets = () => {
  const configuredTargets = (process.env.REACT_APP_SENTRY_TRACE_TARGETS || '')
    .split(',')
    .map(target => target.trim())
    .filter(Boolean);
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  return Array.from(new Set([currentOrigin, ...configuredTargets].filter(Boolean)));
};

const filterContext = context =>
  Object.fromEntries(
    Object.entries(context).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const isSentryEnabled = () => Boolean(SENTRY_DSN);

export const initSentry = () => {
  if (!isSentryEnabled()) return false;

  const integrations = [Sentry.browserTracingIntegration()];
  const replaysSessionSampleRate = parseSampleRate(
    process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
    0
  );
  const replaysOnErrorSampleRate = parseSampleRate(
    process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE,
    1
  );

  if (replaysSessionSampleRate > 0 || replaysOnErrorSampleRate > 0) {
    integrations.push(
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      })
    );
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE || undefined,
    debug: parseBoolean(process.env.REACT_APP_SENTRY_DEBUG, false),
    integrations,
    tracesSampleRate: parseSampleRate(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE, 0.1),
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    tracePropagationTargets: getTracePropagationTargets(),
    sendDefaultPii: false,
    initialScope: {
      tags: {
        app: 'get-untethered',
        runtime: 'browser',
      },
    },
  });

  return true;
};

export const captureBoundaryException = (error, errorInfo) => {
  if (!isSentryEnabled()) return;
  Sentry.captureReactException(error, errorInfo);
};

export const setObservabilityTag = (key, value) => {
  if (!isSentryEnabled() || value === undefined || value === null || value === '') return;
  Sentry.setTag(key, String(value));
};

export const setObservabilityContext = (name, context) => {
  if (!isSentryEnabled()) return;
  const filtered = filterContext(context);
  Sentry.setContext(name, Object.keys(filtered).length ? filtered : null);
};
